import { useInView } from "react-intersection-observer";
import { useState } from "react";
import {
  TrendingUp, Shield, Landmark, CheckCircle, ArrowRight,
  BarChart3, Heart, Car, Umbrella, PiggyBank, Calculator, MessageCircle
} from "lucide-react";
import LeadCaptureModal from "@/components/LeadCaptureModal";

function ScrollReveal({ children, className, delay = 0 }) {
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.1 });
  return (
    <div
      ref={ref}
      className={className || ""}
      style={{
        opacity: inView ? 1 : 0,
        transform: inView ? "translateY(0)" : "translateY(32px)",
        transition: `opacity 0.7s ease-out ${delay}ms, transform 0.7s ease-out ${delay}ms`,
      }}
    >
      {children}
    </div>
  );
}

const SERVICES = [
  {
    id: "mutual-funds",
    icon: TrendingUp,
    accent: "#3B82F6",
    title: "Mutual Fund Advisory",
    subtitle: "Build Wealth Systematically",
    desc: "We help you navigate the world of mutual funds with personalized portfolio strategies designed for your risk profile and financial goals.",
    features: [
      { icon: BarChart3, text: "SIP & Lump Sum Investments" },
      { icon: TrendingUp, text: "Equity, Debt & Hybrid Funds" },
      { icon: PiggyBank, text: "Goal-based Investment Planning" },
      { icon: Calculator, text: "Regular Portfolio Rebalancing" },
    ],
    details: [
      "Customized SIP strategies starting from Rs 500/month",
      "Diversified portfolio across Large Cap, Mid Cap, and Small Cap funds",
      "Regular performance reviews and fund switching when needed",
      "Retirement, child education, and wealth creation goal mapping",
    ]
  },
  {
    id: "insurance",
    icon: Shield,
    accent: "#10B981",
    title: "Insurance Solutions",
    subtitle: "Protect What Matters Most",
    desc: "As a RenewBuy & NJ Group partner, we offer comprehensive insurance solutions from leading insurers to safeguard your family's financial well-being.",
    features: [
      { icon: Heart, text: "Health Insurance" },
      { icon: Umbrella, text: "Life Insurance" },
      { icon: Car, text: "Motor Insurance" },
      { icon: Shield, text: "Critical Illness Cover" },
    ],
    details: [
      "Compare plans from 20+ leading insurance companies",
      "Cashless hospitalization at 10,000+ network hospitals",
      "Family floater plans with comprehensive coverage",
      "Claim assistance and policy renewal reminders",
    ]
  },
  {
    id: "tax-planning",
    icon: Landmark,
    accent: "#D4AF37",
    title: "Tax Planning & ELSS",
    subtitle: "Save Tax, Build Wealth",
    desc: "Smart tax planning goes beyond Section 80C. We help you maximize savings legally while building a strong investment portfolio.",
    features: [
      { icon: Landmark, text: "ELSS Mutual Funds" },
      { icon: Calculator, text: "Tax Regime Analysis" },
      { icon: PiggyBank, text: "NPS & PPF Planning" },
      { icon: BarChart3, text: "Section 80C Optimization" },
    ],
    details: [
      "ELSS investments with just 3-year lock-in period",
      "Old vs New tax regime comparison for your income",
      "Maximize deductions under 80C, 80D, and 80CCD",
      "Year-round tax planning, not just March rush",
    ]
  },
];

export default function ServicesPage() {
  const [showLeadModal, setShowLeadModal] = useState(false);

  return (
    <div data-testid="services-page" className="pt-20">
      {/* Hero */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-[#F9FAFB] via-white to-[#EFF6FF]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <ScrollReveal>
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#3B82F6] mb-3 block">
              Our Services
            </span>
            <h1 className="font-['Outfit'] text-4xl sm:text-5xl font-semibold tracking-tight text-[#0B1F3A] mb-4">
              Comprehensive Financial Solutions
            </h1>
            <p className="text-base md:text-lg text-gray-600 max-w-2xl mx-auto">
              From wealth creation to family protection and tax optimization,
              we offer end-to-end financial advisory services.
            </p>
          </ScrollReveal>
        </div>
      </section>

      {/* Services */}
      {SERVICES.map((service, idx) => (
        <section
          key={service.id}
          id={service.id}
          className={`py-20 md:py-28 ${idx % 2 === 0 ? "bg-white" : "bg-[#F9FAFB]"}`}
          data-testid={`service-section-${service.id}`}
        >
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className={`grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start ${idx % 2 !== 0 ? "lg:direction-rtl" : ""}`}>
              {/* Content */}
              <div className={idx % 2 !== 0 ? "lg:order-2" : ""}>
                <ScrollReveal>
                  <div
                    className="w-14 h-14 rounded-2xl flex items-center justify-center mb-5"
                    style={{ backgroundColor: `${service.accent}10` }}
                  >
                    <service.icon className="w-6 h-6" style={{ color: service.accent }} />
                  </div>
                  <h2 className="font-['Outfit'] text-3xl md:text-4xl font-semibold tracking-tight text-[#0B1F3A] mb-2">
                    {service.title}
                  </h2>
                  <p className="text-[#3B82F6] font-medium mb-4">{service.subtitle}</p>
                  <p className="text-base md:text-lg text-gray-600 leading-relaxed mb-8">
                    {service.desc}
                  </p>
                </ScrollReveal>

                <ScrollReveal delay={200}>
                  <div className="grid grid-cols-2 gap-4 mb-8">
                    {service.features.map((f) => (
                      <div key={f.text} className="flex items-center gap-2.5 text-sm text-gray-700">
                        <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${service.accent}10` }}>
                          <f.icon className="w-4 h-4" style={{ color: service.accent }} />
                        </div>
                        {f.text}
                      </div>
                    ))}
                  </div>
                </ScrollReveal>

                <ScrollReveal delay={300}>
                  <button
                    onClick={() => setShowLeadModal(true)}
                    className="bg-[#0B1F3A] text-white font-semibold px-7 py-3 rounded-full hover:bg-[#111827] transition-all shadow-md flex items-center gap-2 btn-glow"
                    data-testid={`service-cta-${service.id}`}
                  >
                    Get Started <ArrowRight className="w-4 h-4" />
                  </button>
                </ScrollReveal>
              </div>

              {/* Details card */}
              <div className={idx % 2 !== 0 ? "lg:order-1" : ""}>
                <ScrollReveal delay={200}>
                  <div className="bg-white border border-gray-100 rounded-2xl p-8 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)]">
                    <h3 className="font-['Outfit'] text-lg font-semibold text-[#0B1F3A] mb-5">
                      What You Get
                    </h3>
                    <ul className="space-y-4">
                      {service.details.map((d) => (
                        <li key={d} className="flex items-start gap-3 text-sm text-gray-600">
                          <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: service.accent }} />
                          <span>{d}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </ScrollReveal>
              </div>
            </div>
          </div>
        </section>
      ))}

      {/* Bottom CTA */}
      <section className="py-16 md:py-20 bg-[#0B1F3A]">
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <ScrollReveal>
            <h2 className="font-['Outfit'] text-2xl md:text-3xl font-semibold text-white mb-4">
              Not Sure Which Service You Need?
            </h2>
            <p className="text-gray-400 mb-6">
              Book a free consultation and we'll help you figure out the best plan.
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <button
                onClick={() => setShowLeadModal(true)}
                className="bg-[#3B82F6] text-white font-semibold px-7 py-3 rounded-full hover:bg-[#2563EB] transition-all shadow-md flex items-center gap-2"
                data-testid="services-bottom-cta"
              >
                Get Free Consultation <ArrowRight className="w-4 h-4" />
              </button>
              <a
                href="whatsapp://send?phone=919324555493"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-[#25D366] text-white font-semibold px-7 py-3 rounded-full flex items-center gap-2"
              >
                <MessageCircle className="w-4 h-4" fill="white" /> WhatsApp
              </a>
            </div>
          </ScrollReveal>
        </div>
      </section>

      <LeadCaptureModal open={showLeadModal} onOpenChange={setShowLeadModal} />
    </div>
  );
}
